<template>
  <SheetModal
    class="EditProfileModalView"
    data-theme="light"
    aria-label="Edit your profile"
    :aria-busy="isRequesting || isSaving"
    @close="closeActiveModal()"
  >
    <form
      class="EditProfileModalView__form"
      :aria-busy="isSaving"
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="EditProfileModalView__fieldset"
        :disabled="isSaving"
      >
        <SheetHeader
          title="Edit your profile"
          @cancel="handleCancel"
        />

        <div class="EditProfileModalView__content">
          <FormField
            label="Theme"
          >
            <ThemeCarousel
              v-model="form.theme"
            />
          </FormField>

          <div class="EditProfileModalView__fieldsetGrid">
            <!-- <FormField
            label="Avatar"
            gridArea="avatar"
          >
            <PlayerAvatar
              size="large"
              :modelValue="currentPlayer"
              :isClickable="false"
              :theme="form.theme"
            />
          </FormField> -->

            <FormField
              label="Full name"
              gridArea="name"
            >
              <FormInput
                v-model="form.name"
                name="name"
                autocomplete="name"
                :placeholder="namePlaceholder"
                isRequired
              />
            </FormField>

            <FormField
              label="Jersey #"
              gridArea="jerseyNumber"
            >
              <FormInput
                v-model="form.jersey_number"
                name="jerseyNumber"
                inputMode="numeric"
                textAlign="center"
                :maxLength="2"
                :placeholder="jerseyNumberPlaceholder"
              />
            </FormField>

            <FormField
              label="Position"
              gridArea="position"
            >
              <PositionDropdown
                v-model="form.position"
              />
            </FormField>

            <FormField
              label="Location"
              gridArea="location"
            >
              <FormInput
                v-model="form.location"
                name="location"
                autocomplete="address-level2"
                :placeholder="locationPlaceholder"
              />
            </FormField>

            <FormField
              label="Height"
              gridArea="height"
            >
              <HeightInput
                v-model="form.height"
              />
            </FormField>

            <FormField
              label="Weight"
              gridArea="weight"
            >
              <WeightInput
                v-model="form.weight"
              />
            </FormField>

            <FormField
              label="Highest level played"
              gridArea="experience"
            >
              <ExperienceRangeInput
                v-model="form.experience"
              />
            </FormField>

            <FormField
              label="Venmo username"
              gridArea="venmo"
            >
              <FormInput
                v-model="form.venmo_username"
                prefix="@"
                maxlength="30"
                name="venmo"
                autocapitalize="off"
                autocorrect="off"
                :spellcheck="false"
              />
            </FormField>
          </div>
        </div>
      </fieldset>
    </form>
  </SheetModal>
</template>

<script lang="ts" setup>
import ExperienceRangeInput from "@/components/ExperienceRangeInput.vue";
import FormField from "@/components/FormField.vue";
import FormInput from "@/components/FormInput.vue";
import HeightInput from "@/components/HeightInput.vue";
import PositionDropdown from "@/components/PositionDropdown.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import ThemeCarousel from "@/components/ThemeCarousel.vue";
import WeightInput from "@/components/WeightInput.vue";
import useAppTheme from "@/composables/useAppTheme";
import { supabaseClient } from "@/lib/database/supabase";
import useAuthStore from "@/stores/useAuthStore";
import useModalStore from "@/stores/useModalStore";
import { topPlayerMap } from "@shared/lib/constants/players";
import { Theme } from "@shared/lib/constants/themes";
import { PlayerProfile } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";

const {
  closeActiveModal,
} = useModalStore();

const {
  waitForPlayer,
} = useAuthStore();

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const isRequesting = ref(true);
const isSaving = ref(false);

const form = ref<Omit<PlayerProfile, "id"> & { theme: keyof typeof Theme }>(getInitialForm());

onBeforeMount(async () => {
  await waitForPlayer();
  form.value = getInitialForm();
  isRequesting.value = false;
});

const theme = computed(() => form.value.theme as keyof typeof Theme | null | undefined);
useAppTheme(theme);

const placeholderPlayer = computed(() => {
  return topPlayerMap.get(theme.value || "LosAngelesLakers");
});

const namePlaceholder = computed(() => {
  return placeholderPlayer.value?.name || "Luka Dončić";
});

const jerseyNumberPlaceholder = computed(() => {
  return String(placeholderPlayer.value?.jersey_number || 24);
});

const locationPlaceholder = computed(() => {
  return placeholderPlayer.value?.location || "Los Angeles, CA";
});

function getInitialForm () {
  return {
    name: currentPlayer.value?.name || "",
    position: currentPlayer.value?.position || null,
    theme: (currentPlayer.value?.theme || Theme.LosAngelesLakers) as keyof typeof Theme,
    location: currentPlayer.value?.location || null,
    height: currentPlayer.value?.height || null,
    weight: currentPlayer.value?.weight || null,
    birthdate: currentPlayer.value?.birthdate || null,
    jersey_number: currentPlayer.value?.jersey_number ?? null,
    avatar_storage_path: currentPlayer.value?.avatar_storage_path || null,
    experience: currentPlayer.value?.experience ?? null,
    venmo_username: currentPlayer.value?.venmo_username || null,
  };
}

async function handleSubmit () {
  if (!currentPlayer.value) return;

  isSaving.value = true;

  const payload = {
    ...form.value,
    venmo_username: form.value.venmo_username?.trim() || null,
  };

  const {
    data: updatedPlayer,
    error,
  } = await supabaseClient.from("players")
    .update(payload)
    .eq("id", currentPlayer.value.id)
    .select("*")
    .maybeSingle();

  isSaving.value = false;

  if (updatedPlayer) {
    Object.assign(currentPlayer.value, updatedPlayer);
    // TODO: update profile too
    closeActiveModal();
  } else if (error) {
    alert(error.message);
  }
}

async function handleCancel () {
  if (currentPlayer.value?.theme) {
    form.value.theme = currentPlayer.value.theme as keyof typeof Theme;
  }

  closeActiveModal();
}
</script>

<style lang="scss" scoped>
.EditProfileModalView__form {
  position: absolute;
  inset: 0;
}

.EditProfileModalView__fieldset {
  display: grid;
  align-content: start;
  height: 100%;
}

.EditProfileModalView__content {
  position: relative;

  display: grid;
  gap: 24px;
  min-height: 0;
  padding: 24px 16px calc(24px + var(--safeAreaInsetBottom));
  overflow-y: auto;
}

.EditProfileModalView__fieldsetGrid {
  display: grid;
  grid-template-areas: "name name name name"
    "jerseyNumber position position position"
    "location location location location"
    "height height weight weight"
    "experience experience experience experience"
    "venmo venmo venmo venmo";
  grid-template-columns: repeat(4, 1fr);
  gap: inherit;
}
</style>
